import React, { useState, useEffect } from "react"
import {Link, useParams, useHistory, useLocation, Redirect} from "react-router-dom"
import classnames from "classnames"
import { useSelector } from "react-redux"
import queryString from "query-string"
import Breadcrumbs from "@material-ui/core/Breadcrumbs"
import Typography from "@material-ui/core/Typography"

import styles from "./CompanyOpportunitiesContainer.module.scss"
import services from "../../services"
import OpportunityCard from "../../components/cards/OpportunityCard"
import avatar from "../../images/defaultUserAvatar.png"
import ButtonComponent from "../../components/Button/Button"
import CompanyAddonTypePopup from "../../components/CompanyAddonTypePopup"
import { opportunitiesTypeCards } from "../../components/Opportunity/heplers"
import Pagination from "../../components/elements/Pagination"
import PageLinedTitle from "../../components/elements/PageLinedTitle"
import NoDataPlaceholder from "../../components/elements/NoDataPlaceholder"
import CustomTooltip from "../../components/CompanyUserInfo/CustomTooltip"
import useWindowHeightWidth from "../../hooks/useWindowHeightWidth"
import DropdownListSort from "../../components/DropdownListSort"
import Loading from "../../components/Loading/Loading"
import {COMPANY_SEEKER} from "../../helpers/userRoles";
import {paths} from "../../routes/paths";

const sortOptions = [
  {
    value: "a_to_z",
    label: "A to Z",
    arrow: "up",
  },
  {
    value: "z_to_a",
    label: "Z to A",
    arrow: "down",
  },
  {
    value: "newest",
    label: "Newest",
    arrow: "up",
  },
  {
    value: "oldest",
    label: "Oldest",
    arrow: "down",
  },
]

const CompanyOpportunitiesContainer = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [isPopupOpened, setIsPopupOpened] = useState(false)
  const userInfo = useSelector((state) => state.user)
  const history = useHistory()
  const params = useParams()
  const location = useLocation()
  const { width } = useWindowHeightWidth()
  const queryParams = queryString.parse(location.search)
  const normalizedPathname = location.pathname.replace(/\/+$/, "")
  const isCombinedView = normalizedPathname.endsWith("/all")
  const isPrivateView = queryParams.is_private === 'true'
  const [currentSort, setCurrentSort] = useState(null)

  const currentPage = getCurrentPage(location)
  const isAbleToEdit = userInfo.company.uuid === data["company_uuid"]

  const tooltipText = data?.total_departments_count
    ? null
    : "Please add at least one department before adding an opportunity."

  useEffect(() => {
    if (queryParams.sort_by) {
      setCurrentSort(
        sortOptions.find((option) => option.value === queryParams.sort_by)
          ?.value || sortOptions[2].value,
      )
    } else {
      setCurrentSort(sortOptions[2].value)
    }
  }, [queryParams])

  useEffect(() => {
    setLoading(true)

    // Explicitly set is_private: 'false' for public opportunities, 'true' for private
    // This ensures backend filters correctly
    const requestParams = {
      page: currentPage,
      sort_by: currentSort,
    }
    if (isCombinedView) {
      requestParams.include_private = true
    } else if (isPrivateView) {
      requestParams.is_private = 'true'
    }
    
    services.opportunityServices
      .getOpportunitiesByUuid(params.uuid, requestParams)
      .then((result) => {
        let res = {
          ...result.data,
          opportunities: result.data.opportunities.on_page,
          totalCount: result.data.opportunities.total_count,
        }

        setData(res)
        setLoading(false)
      })
  }, [currentPage, params.uuid, currentSort, isPrivateView, isCombinedView])

  const navigateWithParams = (paramsObj) => {
    const routeParams = queryString.stringify(paramsObj, {
      skipEmptyString: true,
      skipNull: true,
    })
    history.push(`${location.pathname}${routeParams ? `?${routeParams}` : ""}`)
  }

  const handleClick = (e, opportunitie) => {
    if (e.target.dataset.type !== "button") {
      history.push(`/opportunity?id=${opportunitie["uuid"]}`)
    }
  }

  const onSort = (type) => {
    navigateWithParams({
      ...queryParams,
      sort_by: type.value,
    })
  }

  return (
    <div className={styles.opportunitiesContainerWrapper}>
      {isPopupOpened && (
        <CompanyAddonTypePopup
          width={952}
          closeAction={setIsPopupOpened}
          title={isPrivateView ? "Add Private Opportunity" : "Add Opportunity"}
          handleNextClick={(a) =>
            history.push(
              isPrivateView
                ? `/opportunity-edit?variant=${a.type}&is_private=true`
                : `/opportunity-edit?variant=${a.type}`
            )
          }
          data={isPrivateView 
            ? opportunitiesTypeCards.map((card) => ({
                ...card,
                title: `Private ${card.title}`,
              }))
            : opportunitiesTypeCards}
        />
      )}

      {
        <>
          <div className={styles.opportunitiesContainer}>
            <div
              className={classnames("container", "fullWidth", styles.Wrapper)}
            >
              <Breadcrumbs className={styles.breadcrumbs}>
                <Link
                  to={`/company/${data["company_uuid"]}`}
                  className={styles.link}
                >
                  <p>{data["company_name"]}</p>
                </Link>

                <Typography
                  className={classnames(styles.link, styles.currentLink)}
                >
                  <p>
                    {isAbleToEdit
                      ? isPrivateView
                        ? `My Private Opportunities`
                        : `My Opportunities`
                      : "Company/Organization Opportunities"}
                  </p>
                </Typography>
              </Breadcrumbs>
            </div>
          </div>

          <PageLinedTitle
            title={
              isAbleToEdit
                ? isPrivateView
                  ? `My Private Opportunities`
                  : `My Opportunities`
                : "Company/Organization Opportunities"
            }
          />

          <div className={styles.opportunitiesContainer}>
            <div
              className={classnames("container", "fullWidth", styles.Wrapper)}
            >
              <div className={styles.navBar}>
                <div>
                  <DropdownListSort
                    options={sortOptions}
                    selectedValue={currentSort}
                    onChange={onSort}
                  />
                </div>

                <div className={styles.navBarButtonHandler}>
                  <CustomTooltip
                    text={tooltipText}
                    children={
                      <span className={styles.tooltipWrapper}>
                        <ButtonComponent
                          disabled={data?.total_departments_count === 0}
                          buttonStyle="contained"
                          className={classnames(
                            styles.navBarButton,
                            styles.navBarAddPrivateButton,
                            "text16r",
                            !isAbleToEdit && styles.navBarButtonDisabled,
                          )}
                          onClick={() => setIsPopupOpened(true)}
                          height="40px"
                          >
                          {width > 425 
                            ? (isPrivateView ? "Add Private Opportunity" : "Add Opportunity")
                            : (isPrivateView ? "Add Private" : "Add")}
                        </ButtonComponent>
                      </span>
                    }
                  />
                </div>
              </div>
              {loading && <Loading />}

              {data.opportunities?.length === 0 && (
                <NoDataPlaceholder>No opportunities yet</NoDataPlaceholder>
              )}

              {!loading && data.opportunities?.length > 0 && (
                <div className={styles.opportunitiesWrapper}>
                  {data.opportunities.map((opportunity) => (
                    <OpportunityCard
                      key={opportunity["created_at"]}
                      uuid={opportunity.uuid}
                      type={opportunity["opportunity_type"]}
                      avatar={data["company_photo"] || avatar}
                      description={opportunity["description"]}
                      location={{
                        city: opportunity["location"],
                        head: data["company_name"],
                      }}
                      caption={opportunity["name"]}
                      timestamp={new Date(opportunity["created_at"])}
                      onClick={(e) => handleClick(e, opportunity)}
                      isPrivate={opportunity["is_private"] === true}
                    />
                  ))}
                </div>
              )}

              {!loading &&
                data.totalCount !== 0 &&
                getPageCount(data.totalCount) > 1 && (
                  <div style={{ padding: "20px", textAlign: "center" }}>
                    <Pagination
                      currentPageNumber={currentPage}
                      totalPageCount={getPageCount(data.totalCount)}
                      onSelect={(page) => {
                        navigateWithParams({
                          ...queryParams,
                          page,
                        })
                      }}
                    />
                  </div>
                )}
            </div>
          </div>
        </>
      }
    </div>
  )
}

export default CompanyOpportunitiesContainer

const getCurrentPage = (location) => {
  const searchParams = new URLSearchParams(location.search)
  return Number(searchParams.get("page")) || 1
}

const getPageCount = (totalCount) => Math.ceil(totalCount / 6) || 1
